import {
  ColumnDef,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { ChevronDown, ChevronRight } from '@mint-lib/icons';
import { Button, Checkbox } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import {
  Table,
  TableBody,
  TableBodyCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../../molecules/TableComponents/TableComponents.jsx';
import type {
  TableCellDataType,
  TableDataType,
} from './permissionsTable.type.js';

export const AccessLevelsMap = {
  NoAccess: 'no_access_level',
  ReadOnly: 'read_only_level',
  ReadAndWrite: 'read_and_write_level',
};

export const SecondLevelParents = [
  'campaignStrategyFeature',
  'campaignPlanningFeature',
  'integrationsFeature',
  'campaignReportsInsightsFeature',
  'namingConventionFeature',
];

// Indent per nesting level, written out so Tailwind can find the classes.
const levelWidths: Record<number, string> = {
  1: 'w-4',
  2: 'w-8',
  3: 'w-12',
};

const isPermissionChecked = (
  level: string,
  data: TableCellDataType,
  rowLevel?: number,
): boolean => {
  if (data.children && rowLevel !== 1) {
    return data.children.every((childData) =>
      isPermissionChecked(level, childData),
    );
  }

  if (data.children && rowLevel === 1) {
    return data.accessLevel === level;
  }

  return data.accessLevel === level;
};

const changeTabPermission = (
  accessLevel: string,
  activeName: string,
  data: TableCellDataType,
  parents: any,
): TableCellDataType => {
  const parentsState = parents;

  if (SecondLevelParents.includes(data.name)) {
    parentsState[data.name] = {
      accessLevel: data.name === activeName ? accessLevel : data.accessLevel,
      children: data.children?.map((el) => el.name),
    };
  }

  for (const key in parentsState) {
    if (parentsState[key]?.children?.includes(activeName)) {
      if (
        parentsState[key]?.accessLevel === AccessLevelsMap.NoAccess &&
        (accessLevel === AccessLevelsMap.ReadAndWrite ||
          accessLevel === AccessLevelsMap.ReadOnly)
      ) {
        return {
          ...data,
          accessLevel:
            data.name === activeName
              ? AccessLevelsMap.NoAccess
              : data.accessLevel,
        };
      }
      if (
        parentsState[key]?.accessLevel === AccessLevelsMap.ReadOnly &&
        accessLevel === AccessLevelsMap.ReadAndWrite
      ) {
        return {
          ...data,
          accessLevel:
            data.name === activeName
              ? AccessLevelsMap.ReadOnly
              : data.accessLevel,
        };
      }
    }
  }

  if (data.children) {
    // * need to check all children if parent has been checked
    // * for that child.name is passed to the next level in order
    // * to check all nested levels
    const isLevelNameMatchName = data.name === activeName;

    return {
      ...data,
      accessLevel: data.name === activeName ? accessLevel : data.accessLevel,
      children: data.children.map((child) =>
        changeTabPermission(
          accessLevel,
          isLevelNameMatchName ? child.name : activeName,
          child,
          parentsState,
        ),
      ),
    };
  }

  if (
    activeName === 'companyInformationFeature' &&
    accessLevel === AccessLevelsMap.NoAccess
  ) {
    return {
      ...data,
      accessLevel: AccessLevelsMap.ReadOnly,
    };
  }

  return {
    ...data,
    accessLevel: data.name === activeName ? accessLevel : data.accessLevel,
  };
};

type PermissionsTableProps = {
  data: TableDataType;
  setData: (state: TableDataType) => void;
  nameLabels?: {
    [key: string]: string;
  };
  isDisabled?: boolean;
};

const PermissionsTable = ({
  data,
  setData,
  isDisabled,
}: PermissionsTableProps) => {
  const { t } = useTranslation('@myn/permissions');

  const [expanded, setExpanded] = useState<ExpandedState>(true);

  const bottomItems = data?.main?.children?.map((el) => {
    const lastItem = el?.children?.[el?.children?.length - 1];

    if (lastItem) {
      const lastItemChildren =
        lastItem?.children?.[lastItem?.children?.length - 1];

      return lastItemChildren?.name || lastItem?.name;
    }

    return el?.children?.[el?.children?.length - 1]?.name;
  });

  const handleCellChange = (activeLevel: string, cell: TableCellDataType) => {
    const tabState = changeTabPermission(activeLevel, cell.name, data.main, {});

    setData({
      ...data,
      main: tabState,
    });
  };

  const columns: ColumnDef<TableCellDataType>[] = useMemo(
    () => [
      {
        id: 'permission',
        cell: (info) => {
          const { depth } = info.row;
          const canExpand = depth === 0 && !!info.row.original.children;
          const isLast = bottomItems?.includes(info.row.original.name);

          return (
            <>
              {/* Every row keeps the 40px arrow slot, like the MUI expander cell. */}
              <span className="flex h-10 w-10 shrink-0 items-center justify-center">
                {canExpand ? (
                  <Button
                    icon
                    variant="plain"
                    size="medium"
                    aria-label="expand-button"
                    onClick={() => {
                      info.row.toggleExpanded();
                    }}
                  >
                    {info.row.getIsExpanded() ? (
                      <ChevronDown />
                    ) : (
                      <ChevronRight />
                    )}
                  </Button>
                ) : null}
              </span>
              {depth === 0 ? (
                info.row.original.label
              ) : (
                <span
                  className={cn(
                    'relative flex h-10 items-center gap-2 pl-4 before:absolute before:top-0 before:left-6 before:w-px before:bg-ui-tertiary before:content-[""]',
                    isLast ? 'before:h-1/2' : 'before:h-full',
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'ml-2 h-px shrink-0 bg-ui-tertiary',
                      levelWidths[depth] ?? 'w-4',
                    )}
                  />
                  {info.row.original.label}
                </span>
              )}
            </>
          );
        },
        header: () => t('Permission'),
      },
      {
        id: 'access',
        cell: (info) => (
          <div className="flex w-full justify-center">
            {info.row.original?.name === 'companyInformationFeature' ? null : (
              <Checkbox
                className="cursor-pointer"
                checked={isPermissionChecked(
                  AccessLevelsMap.NoAccess,
                  info.row.original as TableCellDataType,
                  info.row.depth,
                )}
                onCheckedChange={() =>
                  handleCellChange(AccessLevelsMap.NoAccess, info.row.original)
                }
                disabled={isDisabled}
              />
            )}
          </div>
        ),
        header: () => t('No access'),
      },
      {
        id: 'readonly',
        cell: (info) => (
          <div className="flex w-full justify-center">
            <Checkbox
              className="cursor-pointer"
              checked={isPermissionChecked(
                AccessLevelsMap.ReadOnly,
                info.row.original as TableCellDataType,
                info.row.depth,
              )}
              onCheckedChange={() =>
                handleCellChange(AccessLevelsMap.ReadOnly, info.row.original)
              }
              disabled={isDisabled}
            />
          </div>
        ),
        header: () => t('Read only'),
      },
      {
        id: 'readwrite',
        cell: (info) => (
          <div className="flex w-full justify-center">
            <Checkbox
              className="cursor-pointer"
              checked={isPermissionChecked(
                AccessLevelsMap.ReadAndWrite,
                info.row.original as TableCellDataType,
                info.row.depth,
              )}
              onCheckedChange={() =>
                handleCellChange(
                  AccessLevelsMap.ReadAndWrite,
                  info.row.original,
                )
              }
              disabled={isDisabled}
            />
          </div>
        ),
        header: () => t('Read & Write'),
      },
    ],
    [t, data],
  );

  const { getHeaderGroups, getRowModel } = useReactTable({
    columns: columns,
    data: data.main.children ? data.main.children : [],
    state: {
      expanded,
    },
    onExpandedChange: (e) => {
      if (setExpanded) {
        setExpanded(e);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row) => (row.children ? row.children : []),
  });

  // This section should be hidden until the NC for the planning section is provided.
  const shouldIncludeRow = (row: Row<TableCellDataType>) =>
    row.original.name !== 'namingConventionPlanningFeature';

  return (
    <div className="flex w-full flex-col">
      <div className="max-h-102.5 overflow-auto">
        <Table>
          <TableHead>
            {getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHeaderCell key={header.id}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </TableHeaderCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {getRowModel()
              .rows.filter(shouldIncludeRow)
              .map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const isPermissionCell = cell.column.id === 'permission';

                    return (
                      <TableBodyCell
                        key={cell.id}
                        className={cn(isPermissionCell && 'pl-0')}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableBodyCell>
                    );
                  })}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default PermissionsTable;
